/**
 * <locale-selector>
 *
 * Unified country / language / currency picker. Merges Shopify's same-domain
 * countries (seeded via Liquid) with cross-domain markets discovered from
 * hreflang tags in <head>. Same-domain picks submit the native
 * <form 'localization'>; cross-domain picks navigate to the preserved URL.
 *
 * The modal + overlay are moved to document.body on connect so they escape
 * any stacking context pinned by sticky-header / transform on the header.
 * Each listbox is position: fixed and positioned on open, so it isn't
 * clipped by the modal's scroll container.
 */

const COUNTRY_CURRENCY = {
  AD: 'EUR', AE: 'AED', AF: 'AFN', AG: 'XCD', AI: 'XCD', AL: 'ALL', AM: 'AMD',
  AO: 'AOA', AR: 'ARS', AT: 'EUR', AU: 'AUD', AW: 'AWG', AX: 'EUR', AZ: 'AZN',
  BA: 'BAM', BB: 'BBD', BD: 'BDT', BE: 'EUR', BF: 'XOF', BG: 'BGN', BH: 'BHD',
  BI: 'BIF', BJ: 'XOF', BM: 'BMD', BN: 'BND', BO: 'BOB', BR: 'BRL', BS: 'BSD',
  BT: 'BTN', BW: 'BWP', BY: 'BYN', BZ: 'BZD', CA: 'CAD', CD: 'CDF', CF: 'XAF',
  CG: 'XAF', CH: 'CHF', CI: 'XOF', CL: 'CLP', CM: 'XAF', CN: 'CNY', CO: 'COP',
  CR: 'CRC', CU: 'CUP', CV: 'CVE', CY: 'EUR', CZ: 'CZK', DE: 'EUR', DJ: 'DJF',
  DK: 'DKK', DM: 'XCD', DO: 'DOP', DZ: 'DZD', EC: 'USD', EE: 'EUR', EG: 'EGP',
  ER: 'ERN', ES: 'EUR', ET: 'ETB', FI: 'EUR', FJ: 'FJD', FK: 'FKP', FO: 'DKK',
  FR: 'EUR', GA: 'XAF', GB: 'GBP', GD: 'XCD', GE: 'GEL', GF: 'EUR', GG: 'GBP',
  GH: 'GHS', GI: 'GIP', GL: 'DKK', GM: 'GMD', GN: 'GNF', GP: 'EUR', GQ: 'XAF',
  GR: 'EUR', GT: 'GTQ', GW: 'XOF', GY: 'GYD', HK: 'HKD', HN: 'HNL', HR: 'EUR',
  HT: 'HTG', HU: 'HUF', ID: 'IDR', IE: 'EUR', IL: 'ILS', IM: 'GBP', IN: 'INR',
  IQ: 'IQD', IS: 'ISK', IT: 'EUR', JE: 'GBP', JM: 'JMD', JO: 'JOD', JP: 'JPY',
  KE: 'KES', KG: 'KGS', KH: 'KHR', KM: 'KMF', KN: 'XCD', KR: 'KRW', KW: 'KWD',
  KY: 'KYD', KZ: 'KZT', LA: 'LAK', LB: 'LBP', LC: 'XCD', LI: 'CHF', LK: 'LKR',
  LR: 'LRD', LS: 'LSL', LT: 'EUR', LU: 'EUR', LV: 'EUR', LY: 'LYD', MA: 'MAD',
  MC: 'EUR', MD: 'MDL', ME: 'EUR', MG: 'MGA', MK: 'MKD', ML: 'XOF', MM: 'MMK',
  MN: 'MNT', MO: 'MOP', MQ: 'EUR', MR: 'MRU', MS: 'XCD', MT: 'EUR', MU: 'MUR',
  MV: 'MVR', MW: 'MWK', MX: 'MXN', MY: 'MYR', MZ: 'MZN', NA: 'NAD', NC: 'XPF',
  NE: 'XOF', NG: 'NGN', NI: 'NIO', NL: 'EUR', NO: 'NOK', NP: 'NPR', NZ: 'NZD',
  OM: 'OMR', PA: 'PAB', PE: 'PEN', PF: 'XPF', PG: 'PGK', PH: 'PHP', PK: 'PKR',
  PL: 'PLN', PT: 'EUR', PY: 'PYG', QA: 'QAR', RE: 'EUR', RO: 'RON', RS: 'RSD',
  RU: 'RUB', RW: 'RWF', SA: 'SAR', SB: 'SBD', SC: 'SCR', SE: 'SEK', SG: 'SGD',
  SI: 'EUR', SK: 'EUR', SL: 'SLL', SM: 'EUR', SN: 'XOF', SR: 'SRD', ST: 'STN',
  SV: 'USD', SZ: 'SZL', TC: 'USD', TD: 'XAF', TG: 'XOF', TH: 'THB', TJ: 'TJS',
  TM: 'TMT', TN: 'TND', TO: 'TOP', TR: 'TRY', TT: 'TTD', TW: 'TWD', TZ: 'TZS',
  UA: 'UAH', UG: 'UGX', US: 'USD', UY: 'UYU', UZ: 'UZS', VA: 'EUR', VC: 'XCD',
  VE: 'VES', VG: 'USD', VN: 'VND', VU: 'VUV', WS: 'WST', XK: 'EUR', YE: 'YER',
  ZA: 'ZAR', ZM: 'ZMW', ZW: 'ZWL',
};

// Primary "home" country for each language. Used only as a fallback when
// the current country doesn't support the language the user just picked,
// so we land on the most expected market (pt → Portugal, es → Spain, …)
// instead of whatever's first alphabetically in the language's country
// list. If the primary isn't among the store's available markets we fall
// back to the first country that does support the language.
const LANG_PRIMARY_COUNTRY = {
  ar: 'SA', bg: 'BG', cs: 'CZ', da: 'DK', de: 'DE', el: 'GR', en: 'GB',
  es: 'ES', et: 'EE', fi: 'FI', fr: 'FR', he: 'IL', hi: 'IN', hr: 'HR',
  hu: 'HU', id: 'ID', it: 'IT', ja: 'JP', ko: 'KR', lt: 'LT', lv: 'LV',
  ms: 'MY', nb: 'NO', nl: 'NL', no: 'NO', pl: 'PL', pt: 'PT', ro: 'RO',
  ru: 'RU', sk: 'SK', sl: 'SI', sv: 'SE', th: 'TH', tr: 'TR', uk: 'UA',
  vi: 'VN', zh: 'CN',
};

function flagEmoji(iso) {
  if (!iso || typeof iso !== 'string' || iso.length !== 2) return '';
  const code = iso.toUpperCase();
  const base = 0x1f1e6;
  return String.fromCodePoint(code.charCodeAt(0) - 65 + base, code.charCodeAt(1) - 65 + base);
}

function safeDisplayName(type, value, localeHint) {
  try {
    const out = new Intl.DisplayNames([localeHint || 'en'], { type }).of(value);
    if (out && out !== value) return out;
  } catch (_) {}
  return null;
}

function countryName(iso, uiLang) {
  return safeDisplayName('region', iso, uiLang) || iso;
}

function languageEndonym(iso) {
  const primary = (iso || '').split('-')[0];
  return safeDisplayName('language', iso, iso) || safeDisplayName('language', primary, primary) || iso;
}

function currencySymbol(iso, uiLang) {
  try {
    const parts = new Intl.NumberFormat(uiLang || 'en', {
      style: 'currency',
      currency: iso,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency');
    if (sym) return sym.value;
  } catch (_) {}
  return iso;
}

function parseHreflang(currentHost) {
  const entries = [];
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => {
    const hreflang = link.getAttribute('hreflang');
    const href = link.getAttribute('href');
    if (!hreflang || !href || hreflang === 'x-default') return;
    let host;
    try {
      host = new URL(href, location.origin).host;
    } catch (_) {
      return;
    }
    if (host === currentHost) return;
    const parts = hreflang.split('-');
    entries.push({
      lang: parts[0].toLowerCase(),
      region: parts[1] ? parts[1].toUpperCase() : null,
      host,
      href,
    });
  });
  return entries;
}

function inferCountryFromHost(host) {
  const match = host && host.match(/\.([a-z]{2})$/i);
  if (!match) return null;
  const tld = match[1].toUpperCase();
  const map = { UK: 'GB' };
  return map[tld] || tld;
}

function buildCrossDomainMarkets(hreflangs, uiLang) {
  const byHost = new Map();
  for (const entry of hreflangs) {
    const iso = entry.region || inferCountryFromHost(entry.host);
    if (!iso) continue;
    if (!byHost.has(entry.host)) {
      const ccy = COUNTRY_CURRENCY[iso] || 'USD';
      byHost.set(entry.host, {
        iso,
        name: countryName(iso, uiLang),
        host: entry.host,
        href: entry.href,
        languages: [],
        currency: { iso: ccy, symbol: currencySymbol(ccy, uiLang) },
        crossDomain: true,
      });
    }
    const market = byHost.get(entry.host);
    if (!market.languages.some((l) => l.iso === entry.lang)) {
      market.languages.push({ iso: entry.lang, endonym: languageEndonym(entry.lang) });
    }
  }
  return Array.from(byHost.values());
}

// Shopify's `country.name` can come back HTML-encoded (e.g. "Bosnia &amp;
// Herzegovina"). Decode any existing entities first, then re-escape for
// safe interpolation into an innerHTML template.
const _entityDecoder = document.createElement('textarea');
function decodeEntities(str) {
  if (str == null) return '';
  _entityDecoder.innerHTML = String(str);
  return _entityDecoder.value;
}
function escapeAttr(str) {
  return decodeEntities(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const FOCUSABLE = 'button, [href], input, select, [tabindex]:not([tabindex="-1"])';
const LISTBOX_GAP = 4; // px between combobox button and listbox
const SEARCH_THRESHOLD = 8; // show the search input when a dropdown has more items than this

if (!customElements.get('locale-selector')) {
  customElements.define(
    'locale-selector',
    class LocaleSelector extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        const payloadEl = this.querySelector('.locale-selector__payload');
        const tmpl = this.querySelector('[data-modal-template]');
        if (!payloadEl || !tmpl) return;

        try {
          this.payload = JSON.parse(payloadEl.textContent);
        } catch (err) {
          console.error('[locale-selector] invalid payload', err);
          return;
        }

        this.uiLang = this.payload.uiLanguage || 'en';
        this.currentHost = this.payload.currentHost;

        const crossDomain = buildCrossDomainMarkets(parseHreflang(this.currentHost), this.uiLang);
        this.countries = this.mergeCountries(
          Array.isArray(this.payload.countries) ? this.payload.countries : [],
          crossDomain
        );
        this.languages = this.buildLanguageIndex();
        this.currencies = this.buildCurrencyIndex();
        this.state = {
          country: this.payload.currentCountry,
          language: this.payload.currentLanguage,
          currency: this.payload.currentCurrency,
        };

        this.portal = tmpl.content.firstElementChild.cloneNode(true);
        document.body.appendChild(this.portal);

        this.refs = {
          trigger: this.querySelector('[data-trigger]'),
          triggerFlag: this.querySelector('[data-trigger-flag]'),
          triggerLabel: this.querySelector('[data-trigger-label]'),
          overlay: this.portal.querySelector('[data-overlay]'),
          modal: this.portal.querySelector('[data-modal]'),
          close: this.portal.querySelector('[data-close]'),
          form: this.portal.querySelector('.locale-selector__form'),
          countryInput: this.portal.querySelector('[data-country-input]'),
          languageInput: this.portal.querySelector('[data-language-input]'),
          fields: {
            country: this.portal.querySelector('[data-field="country"]'),
            language: this.portal.querySelector('[data-field="language"]'),
            currency: this.portal.querySelector('[data-field="currency"]'),
          },
        };

        // Move each listbox out of the transformed modal into the portal root.
        // `position: fixed` inside an ancestor with a `transform` is anchored
        // to that ancestor, not the viewport — moving them here frees them.
        this.listboxes = {};
        Object.entries(this.refs.fields).forEach(([field, wrap]) => {
          const listbox = wrap.querySelector('[data-listbox]');
          this.listboxes[field] = listbox;
          this.portal.appendChild(listbox);
        });

        this.updateTrigger();
        this.renderAllOptions();
        this.bindEvents();
      }

      disconnectedCallback() {
        if (this.portal && this.portal.parentNode) {
          this.portal.parentNode.removeChild(this.portal);
        }
      }

      mergeCountries(same, cross) {
        const merged = same.map((c) => ({
          ...c,
          name: decodeEntities(c.name),
          currency: { ...c.currency, symbol: decodeEntities(c.currency && c.currency.symbol) },
          languages: (c.languages || []).map((l) => ({ ...l, endonym: decodeEntities(l.endonym) })),
          crossDomain: false,
        }));
        for (const market of cross) {
          const existing = merged.find((c) => c.iso === market.iso);
          if (existing) {
            for (const lang of market.languages) {
              if (!existing.languages.some((l) => l.iso === lang.iso)) {
                existing.languages.push(lang);
              }
            }
            existing.crossDomainHref = market.href;
          } else {
            merged.push(market);
          }
        }
        merged.sort((a, b) => a.name.localeCompare(b.name, this.uiLang));
        return merged;
      }

      // Collect every language across every country. Mark each with the
      // list of countries that support it, whether it is region-restricted
      // (only one country), and the name of that region if so.
      buildLanguageIndex() {
        const map = new Map();
        for (const country of this.countries) {
          for (const lang of country.languages || []) {
            if (!map.has(lang.iso)) {
              map.set(lang.iso, { iso: lang.iso, endonym: lang.endonym, countries: [] });
            }
            map.get(lang.iso).countries.push({ iso: country.iso, name: country.name });
          }
        }
        return Array.from(map.values()).map((lang) => ({
          ...lang,
          regional: lang.countries.length === 1,
          regionLabel: lang.countries.length === 1 ? lang.countries[0].name : null,
        }));
      }

      // Same idea as buildLanguageIndex but for currencies. Markets-with-
      // only-one-country currencies (e.g. GBP where only UK uses it here)
      // carry a region label; widely-shared ones (e.g. EUR across the
      // eurozone) don't.
      buildCurrencyIndex() {
        const map = new Map();
        for (const country of this.countries) {
          const ccy = country.currency;
          if (!ccy || !ccy.iso) continue;
          if (!map.has(ccy.iso)) {
            map.set(ccy.iso, { iso: ccy.iso, symbol: ccy.symbol, countries: [] });
          }
          map.get(ccy.iso).countries.push({ iso: country.iso, name: country.name });
        }
        return Array.from(map.values())
          .map((c) => ({
            ...c,
            regional: c.countries.length === 1,
            regionLabel: c.countries.length === 1 ? c.countries[0].name : null,
          }))
          .sort((a, b) => a.iso.localeCompare(b.iso));
      }

      getCountry(iso) {
        return this.countries.find((c) => c.iso === iso) || null;
      }

      currentCountryData() {
        return (
          this.getCountry(this.state.country) ||
          {
            iso: this.state.country,
            name: this.payload.currentCountryName || this.state.country,
            currency: {
              iso: this.state.currency,
              symbol: this.payload.currentCurrencySymbol || this.state.currency,
            },
            languages: [
              {
                iso: this.state.language,
                endonym: this.payload.currentLanguageEndonym || this.state.language,
              },
            ],
          }
        );
      }

      updateTrigger() {
        const country = this.currentCountryData();
        this.refs.triggerFlag.textContent = flagEmoji(country.iso) || country.iso;
        const lang = (this.state.language || '').toUpperCase();
        this.refs.triggerLabel.textContent = `${lang}/${this.state.currency}`;
      }

      bindEvents() {
        this.refs.trigger.addEventListener('click', () => this.openModal());
        this.refs.close.addEventListener('click', () => this.closeModal());
        this.refs.overlay.addEventListener('click', () => this.closeModal());
        document.addEventListener('keydown', (e) => this.onKeydown(e));

        Object.entries(this.refs.fields).forEach(([field, wrap]) => {
          const btn = wrap.querySelector('[data-combobox-btn]');
          const listbox = this.listboxes[field];
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = btn.getAttribute('aria-expanded') !== 'true';
            this.toggleListbox(field, open);
          });
          listbox.addEventListener('click', (e) => {
            const option = e.target.closest('[data-value]');
            if (!option) return;
            this.onOptionPick(field, option.dataset.value);
          });
          const search = listbox.querySelector('[data-search]');
          if (search) {
            search.addEventListener('input', () => this.filterOptions(field));
            search.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') e.preventDefault();
            });
          }
        });

        // Close any open listbox when clicking outside of all comboboxes/listboxes
        document.addEventListener('click', (e) => {
          if (!this.portal || !this.portal.contains(e.target)) return;
          if (e.target.closest('[data-field]')) return;
          if (e.target.closest('[data-listbox]')) return;
          this.closeAllListboxes();
        });

        this.refs.form.addEventListener('submit', (e) => this.onSubmit(e));

        window.addEventListener('resize', () => this.repositionListboxes(), { passive: true });
      }

      openModal() {
        this.portal.classList.add('is-open');
        document.body.classList.add('locale-selector-open');
        this.refs.trigger.setAttribute('aria-expanded', 'true');
        this.renderAllOptions();
        this.previouslyFocused = document.activeElement;
        setTimeout(() => {
          this.refs.fields.country.querySelector('[data-combobox-btn]').focus();
        }, 10);
      }

      closeModal() {
        this.portal.classList.remove('is-open');
        document.body.classList.remove('locale-selector-open');
        this.refs.trigger.setAttribute('aria-expanded', 'false');
        this.closeAllListboxes();
        if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
          this.previouslyFocused.focus();
        }
      }

      onKeydown(e) {
        if (!this.portal.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          const openListboxBtn = this.portal.querySelector('[data-combobox-btn][aria-expanded="true"]');
          if (openListboxBtn) {
            this.closeAllListboxes();
            openListboxBtn.focus();
          } else {
            this.closeModal();
          }
          return;
        }
        if (e.key === 'Tab') this.trapFocus(e);
      }

      trapFocus(e) {
        const focusable = Array.from(this.refs.modal.querySelectorAll(FOCUSABLE)).filter(
          (el) => !el.disabled && el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      toggleListbox(field, open) {
        this.closeAllListboxes(field);
        const wrap = this.refs.fields[field];
        const btn = wrap.querySelector('[data-combobox-btn]');
        const listbox = this.listboxes[field];
        listbox.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        if (open) {
          this.positionListbox(field);
          const search = listbox.querySelector('[data-search]');
          if (search && !search.hidden && !search.closest('[data-search-row]').hidden) {
            search.value = '';
            this.filterOptions(field);
            setTimeout(() => search.focus(), 0);
          }
        }
      }

      closeAllListboxes(except) {
        Object.entries(this.refs.fields).forEach(([field, wrap]) => {
          if (field === except) return;
          const btn = wrap.querySelector('[data-combobox-btn]');
          const listbox = this.listboxes[field];
          if (listbox) listbox.hidden = true;
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      // Compute the listbox's viewport position from the button's rect, and
      // flip it upward if there isn't enough room below.
      positionListbox(field) {
        const wrap = this.refs.fields[field];
        const btn = wrap.querySelector('[data-combobox-btn]');
        const listbox = this.listboxes[field];
        if (!btn || !listbox || listbox.hidden) return;

        const btnRect = btn.getBoundingClientRect();
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        const maxHeight = Math.min(320, vh - 24);
        const spaceBelow = vh - btnRect.bottom - LISTBOX_GAP;
        const spaceAbove = btnRect.top - LISTBOX_GAP;

        const flipUp = spaceBelow < 200 && spaceAbove > spaceBelow;
        listbox.style.left = `${Math.max(8, btnRect.left)}px`;
        listbox.style.width = `${Math.min(btnRect.width, vw - 16)}px`;
        if (flipUp) {
          listbox.style.bottom = `${vh - btnRect.top + LISTBOX_GAP}px`;
          listbox.style.top = 'auto';
          listbox.style.maxHeight = `${Math.min(maxHeight, spaceAbove)}px`;
        } else {
          listbox.style.top = `${btnRect.bottom + LISTBOX_GAP}px`;
          listbox.style.bottom = 'auto';
          listbox.style.maxHeight = `${Math.min(maxHeight, spaceBelow)}px`;
        }
      }

      repositionListboxes() {
        Object.keys(this.refs.fields).forEach((field) => this.positionListbox(field));
      }

      renderAllOptions() {
        this.renderCountryOptions();
        this.renderLanguageOptions();
        this.renderCurrencyOptions();
        this.updateSelectedLabels();
        this.updateSearchVisibility();
      }

      renderCountryOptions() {
        const listEl = this.listboxes.country.querySelector('[data-options]');
        listEl.innerHTML = this.countries.map((c) => this.countryOptionHTML(c)).join('');
      }

      countryOptionHTML(country) {
        const isActive = country.iso === this.state.country;
        const searchText = `${country.name} ${country.iso}`.toLowerCase();
        return `<li role="option" class="locale-selector__option${isActive ? ' is-active' : ''}" data-value="${escapeAttr(country.iso)}" data-search-text="${escapeAttr(searchText)}" aria-selected="${isActive}">
          <span class="locale-selector__flag" aria-hidden="true">${flagEmoji(country.iso)}</span>
          <span class="locale-selector__option-label">${escapeAttr(country.name)}</span>
          <span class="locale-selector__option-meta">${escapeAttr(country.currency.iso)}</span>
        </li>`;
      }

      renderLanguageOptions() {
        const list = this.listboxes.language.querySelector('[data-options]');
        list.innerHTML = this.languages
          .map((lang) => {
            const active = lang.iso === this.state.language;
            const sub = lang.regional
              ? `<span class="locale-selector__option-sub">${escapeAttr(lang.regionLabel)}</span>`
              : '';
            const searchText = `${lang.endonym} ${lang.iso} ${lang.regionLabel || ''}`.toLowerCase();
            return `<li role="option" class="locale-selector__option${active ? ' is-active' : ''}" data-value="${escapeAttr(lang.iso)}" data-search-text="${escapeAttr(searchText)}" aria-selected="${active}">
              <span class="locale-selector__option-label">${escapeAttr(lang.endonym)}${sub}</span>
              <span class="locale-selector__option-meta">${escapeAttr((lang.iso || '').toUpperCase())}</span>
            </li>`;
          })
          .join('');
      }

      renderCurrencyOptions() {
        const list = this.listboxes.currency.querySelector('[data-options]');
        list.innerHTML = this.currencies
          .map((c) => {
            const active = c.iso === this.state.currency;
            const sub = c.regional
              ? `<span class="locale-selector__option-sub">${escapeAttr(c.regionLabel)}</span>`
              : '';
            const searchText = `${c.iso} ${c.symbol} ${c.regionLabel || ''}`.toLowerCase();
            return `<li role="option" class="locale-selector__option${active ? ' is-active' : ''}" data-value="${escapeAttr(c.iso)}" data-search-text="${escapeAttr(searchText)}" aria-selected="${active}">
              <span class="locale-selector__option-label">${escapeAttr(c.symbol)} ${escapeAttr(c.iso)}${sub}</span>
            </li>`;
          })
          .join('');
      }

      // Show the listbox's search row when options exceed SEARCH_THRESHOLD.
      // Called from renderAllOptions so the row stays in sync as options change.
      updateSearchVisibility() {
        Object.keys(this.refs.fields).forEach((field) => {
          const listbox = this.listboxes[field];
          const row = listbox.querySelector('[data-search-row]');
          if (!row) return;
          const count = listbox.querySelectorAll('.locale-selector__option').length;
          row.hidden = count <= SEARCH_THRESHOLD;
        });
      }

      updateSelectedLabels() {
        const country = this.currentCountryData();
        const lang = this.languages.find((l) => l.iso === this.state.language);

        const cb = this.refs.fields.country;
        const flagEl = cb.querySelector('[data-selected-flag]');
        if (flagEl) flagEl.textContent = flagEmoji(country.iso) || '';
        cb.querySelector('[data-selected-label]').textContent = decodeEntities(country.name) || country.iso || '';

        const langLabelEl = this.refs.fields.language.querySelector('[data-selected-label]');
        const langSubEl = this.refs.fields.language.querySelector('[data-selected-sub]');
        if (lang) {
          langLabelEl.textContent = decodeEntities(lang.endonym);
          if (langSubEl) langSubEl.textContent = lang.regional ? decodeEntities(lang.regionLabel) : '';
        } else {
          langLabelEl.textContent = decodeEntities(this.payload.currentLanguageEndonym) || this.state.language || '';
          if (langSubEl) langSubEl.textContent = '';
        }

        const curEntry = this.currencies.find((c) => c.iso === this.state.currency);
        const cur = this.refs.fields.currency;
        const curLabelEl = cur.querySelector('[data-selected-label]');
        const curSubEl = cur.querySelector('[data-selected-sub]');
        if (curEntry) {
          curLabelEl.textContent = `${curEntry.symbol || ''} ${curEntry.iso}`.trim();
          if (curSubEl) curSubEl.textContent = curEntry.regional ? decodeEntities(curEntry.regionLabel) : '';
        } else if (country.currency) {
          curLabelEl.textContent = `${country.currency.symbol || ''} ${country.currency.iso || ''}`.trim();
          if (curSubEl) curSubEl.textContent = '';
        }
      }

      // Picking a language switches the country too when the language is
      // only supported in one market (e.g. Deutsch → Germany / drinksalte.de).
      onOptionPick(field, value) {
        if (field === 'country') {
          const country = this.getCountry(value);
          if (!country) return;
          this.state.country = value;
          const supportsCurrent = (country.languages || []).some((l) => l.iso === this.state.language);
          if (!supportsCurrent && country.languages && country.languages[0]) {
            this.state.language = country.languages[0].iso;
          }
          this.state.currency = country.currency.iso;
        } else if (field === 'language') {
          this.state.language = value;
          const current = this.getCountry(this.state.country);
          const currentSupports = current && (current.languages || []).some((l) => l.iso === value);
          if (!currentSupports) {
            // Prefer the language's "home" country (pt → PT, es → ES, …)
            // if the store offers it; otherwise fall back to the first
            // country in the language's list.
            const lang = this.languages.find((l) => l.iso === value);
            const primaryIso = LANG_PRIMARY_COUNTRY[(value || '').split('-')[0].toLowerCase()];
            const preferred = primaryIso && lang && lang.countries.find((c) => c.iso === primaryIso);
            const pick = preferred || (lang && lang.countries[0]);
            if (pick) {
              const target = this.getCountry(pick.iso);
              if (target) {
                this.state.country = target.iso;
                this.state.currency = target.currency.iso;
              }
            }
          }
        } else if (field === 'currency') {
          this.state.currency = value;
          const current = this.getCountry(this.state.country);
          const currentUses = current && current.currency && current.currency.iso === value;
          if (!currentUses) {
            const ccy = this.currencies.find((c) => c.iso === value);
            if (ccy && ccy.countries[0]) {
              const target = this.getCountry(ccy.countries[0].iso);
              if (target) {
                this.state.country = target.iso;
                const supportsLang = (target.languages || []).some((l) => l.iso === this.state.language);
                if (!supportsLang && target.languages && target.languages[0]) {
                  this.state.language = target.languages[0].iso;
                }
              }
            }
          }
        }
        this.closeAllListboxes();
        this.renderAllOptions();
      }

      filterOptions(field) {
        const listbox = this.listboxes[field];
        const search = listbox.querySelector('[data-search]');
        const term = (search && search.value || '').trim().toLowerCase();
        const options = listbox.querySelectorAll('.locale-selector__option');
        let visible = 0;
        options.forEach((opt) => {
          const hay = opt.dataset.searchText || '';
          const match = term.length === 0 || hay.indexOf(term) !== -1;
          opt.hidden = !match;
          if (match) visible++;
        });
        const empty = listbox.querySelector('[data-empty]');
        if (empty) empty.hidden = visible !== 0;
      }

      onSubmit(e) {
        const country = this.getCountry(this.state.country);
        if (country && (country.crossDomain || country.crossDomainHref)) {
          e.preventDefault();
          const href = country.crossDomainHref || country.href;
          if (href) window.location.href = href;
          return;
        }
        this.refs.countryInput.value = this.state.country;
        this.refs.languageInput.value = this.state.language;
      }
    }
  );
}
